import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  useInstagramAudio,
  useInstagramAudioAsset,
  useTikTokCommercialMusic,
  type InstagramAudioAsset,
  type TikTokCommercialTrack,
} from "@/hooks/use-zernio-options";

/**
 * Reach knobs for Instagram (catalog audio, Trial Reels, paid partnership,
 * comments, location) and TikTok (Commercial Music Library). Values are
 * stored on the piece's platformKnobs under Zernio's field names; display
 * fields (title, artist) ride along on the audio objects and are stripped by
 * the publish adapters.
 */

function Label({ children }: { children: React.ReactNode }) {
  return <label className="text-xs font-medium uppercase text-muted-foreground">{children}</label>;
}
function Hint({ children, tone = "muted" }: { children: React.ReactNode; tone?: "muted" | "warn" }) {
  return (
    <div className={`text-[10px] ${tone === "warn" ? "text-amber-600" : "text-muted-foreground"}`}>{children}</div>
  );
}

function clampPercent(raw: string): number | undefined {
  if (raw.trim() === "") return undefined;
  const n = Math.round(Number(raw));
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : undefined;
}

function PercentInput({
  label,
  value,
  placeholder,
  onChange,
  disabled,
}: {
  label: string;
  value: number | undefined;
  placeholder: string;
  onChange: (v: number | undefined) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
      {label}
      <input
        type="number"
        min={0}
        max={100}
        value={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(clampPercent(e.target.value))}
        className="w-16 rounded border px-1 py-0.5 text-xs"
      />
    </label>
  );
}

const fmtMs = (ms: number | null | undefined) => {
  if (!ms) return "";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// ─── Instagram catalog audio (Reels) ─────────────────────────

export interface InstagramAudioKnobValue {
  audioId: string;
  audioVolume?: number;
  videoVolume?: number;
  title?: string | null;
  artist?: string | null;
  audioType?: string | null;
  durationInMs?: number | null;
}

export function InstagramAudioKnob({
  brandId,
  value,
  onChange,
  disabled,
}: {
  brandId: string;
  value: InstagramAudioKnobValue | null | undefined;
  onChange: (next: InstagramAudioKnobValue | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("compose");
  const [open, setOpen] = useState(false);
  const [audioType, setAudioType] = useState<"music" | "original_sound">("music");
  const [draftQ, setDraftQ] = useState("");
  const [q, setQ] = useState("");
  const list = useInstagramAudio(brandId, audioType, q, open && !disabled);
  const selectedId = value?.audioId ?? null;
  const current = useInstagramAudioAsset(brandId, selectedId);
  const fbLogin = list.data?.requiresFacebookLogin || current.data?.requiresFacebookLogin;

  const pick = (a: InstagramAudioAsset) => {
    onChange({
      audioId: a.audioId,
      title: a.title,
      artist: a.artist,
      audioType: a.audioType,
      durationInMs: a.durationInMs,
      ...(value?.audioVolume !== undefined ? { audioVolume: value.audioVolume } : {}),
      ...(value?.videoVolume !== undefined ? { videoVolume: value.videoVolume } : {}),
    });
    setOpen(false);
  };

  return (
    <div className="space-y-1">
      <Label>{t("knobs.igAudio")}</Label>
      {selectedId ? (
        <div className="rounded border p-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{value?.title ?? selectedId}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {[value?.artist, fmtMs(value?.durationInMs)].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setOpen(!open)}>
                {t("knobs.igAudioChange")}
              </Button>
              <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}>
                {t("knobs.remove")}
              </Button>
            </div>
          </div>
          {current.data?.audio?.downloadUrl && (
            <audio controls preload="none" src={current.data.audio.downloadUrl} className="w-full h-8" />
          )}
          {current.data && !current.data.available && !current.data.requiresFacebookLogin && (
            <Hint tone="warn">{t("knobs.igAudioUnavailable")}</Hint>
          )}
          <div className="flex flex-wrap gap-3">
            <PercentInput
              label={t("knobs.igAudioTrackVolume")}
              value={value?.audioVolume}
              placeholder="100"
              disabled={disabled}
              onChange={(v) => onChange({ ...value!, audioVolume: v })}
            />
            <PercentInput
              label={t("knobs.igAudioVideoVolume")}
              value={value?.videoVolume}
              placeholder="100"
              disabled={disabled}
              onChange={(v) => onChange({ ...value!, videoVolume: v })}
            />
          </div>
          <Hint>{t("knobs.igAudioVolumeHint")}</Hint>
        </div>
      ) : (
        !open && (
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setOpen(true)}>
            {t("knobs.igAudioPick")}
          </Button>
        )
      )}

      {fbLogin && (
        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-[11px] text-amber-800 dark:text-amber-300">
          {t("knobs.igFacebookLoginRequired")}
        </div>
      )}

      {open && !fbLogin && (
        <div className="rounded border p-2 space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={audioType}
              onChange={(e) => setAudioType(e.target.value as "music" | "original_sound")}
              className="rounded border px-2 py-1 text-xs"
            >
              <option value="music">{t("knobs.igAudioMusic")}</option>
              <option value="original_sound">{t("knobs.igAudioOriginal")}</option>
            </select>
            <form
              className="flex flex-1 gap-1 min-w-[160px]"
              onSubmit={(e) => {
                e.preventDefault();
                setQ(draftQ.trim());
              }}
            >
              <input
                type="search"
                value={draftQ}
                placeholder={t("knobs.igAudioSearch")}
                onChange={(e) => {
                  setDraftQ(e.target.value);
                  if (!e.target.value) setQ("");
                }}
                className="flex-1 rounded border px-2 py-1 text-xs"
              />
              <Button type="submit" variant="outline" size="sm">
                {t("knobs.search")}
              </Button>
            </form>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t("knobs.close")}
            </Button>
          </div>
          <Hint>{q ? t("knobs.igAudioResults", { q }) : t("knobs.trending")}</Hint>
          {list.isLoading && <Hint>{t("knobs.loading")}</Hint>}
          {list.isError && <Hint tone="warn">{t("knobs.loadFailed")}</Hint>}
          {list.data && !list.data.connected && <Hint tone="warn">{t("knobs.igNotConnected")}</Hint>}
          <div className="max-h-64 overflow-y-auto divide-y">
            {(list.data?.audio ?? []).map((a) => (
              <div key={a.audioId} className="flex items-center gap-2 py-1.5">
                {a.coverUrl ? (
                  <img src={a.coverUrl} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-muted shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{a.title ?? a.audioId}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {[a.artist, fmtMs(a.durationInMs)].filter(Boolean).join(" · ")}
                  </div>
                  {a.downloadUrl && <audio controls preload="none" src={a.downloadUrl} className="w-full h-7 mt-1" />}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={a.audioId === selectedId ? "secondary" : "outline"}
                  onClick={() => pick(a)}
                >
                  {a.audioId === selectedId ? t("knobs.selected") : t("knobs.use")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Instagram Trial Reels ───────────────────────────────────

export function InstagramTrialKnob({
  value,
  onChange,
  disabled,
}: {
  value: { graduationStrategy?: string } | null | undefined;
  onChange: (next: { graduationStrategy: string } | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("compose");
  const strategy = value?.graduationStrategy ?? "";
  return (
    <div className="space-y-1">
      <Label>{t("knobs.igTrial")}</Label>
      <select
        value={strategy}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value ? { graduationStrategy: e.target.value } : null)}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        <option value="">{t("knobs.igTrialOff")}</option>
        <option value="SS_PERFORMANCE">{t("knobs.igTrialAuto")}</option>
        <option value="MANUAL">{t("knobs.igTrialManual")}</option>
      </select>
      <Hint>{t("knobs.igTrialHint")}</Hint>
    </div>
  );
}

// ─── Instagram paid partnership / sponsors ───────────────────

export function InstagramPaidPartnershipKnob({
  value,
  sponsors,
  onChange,
  disabled,
}: {
  value: boolean | null | undefined;
  sponsors: string[];
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("compose");
  const implied = sponsors.length > 0;
  return (
    <div className="space-y-0.5">
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={implied || value === true}
          disabled={disabled || implied}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="font-medium uppercase text-muted-foreground">{t("knobs.igPaidPartnership")}</span>
      </label>
      <Hint>{implied ? t("knobs.igPaidPartnershipImplied") : t("knobs.igPaidPartnershipHint")}</Hint>
    </div>
  );
}

export function InstagramSponsorsKnob({
  value,
  onChange,
  disabled,
}: {
  value: string[] | string | null | undefined;
  onChange: (next: string[] | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("compose");
  const initial = Array.isArray(value) ? value.join(", ") : (value ?? "");
  const [text, setText] = useState(initial);
  useEffect(() => setText(initial), [initial]);
  const parsed = text
    .split(/[\s,]+/)
    .map((s) => s.trim().replace(/^@/, ""))
    .filter(Boolean);
  return (
    <div className="space-y-1">
      <Label>{t("knobs.igSponsors")}</Label>
      <input
        type="text"
        value={text}
        disabled={disabled}
        placeholder="@brand"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onChange(parsed.length ? parsed : null)}
        className="block w-full rounded border px-3 py-2 text-sm"
      />
      {parsed.length > 2 ? (
        <Hint tone="warn">{t("knobs.igSponsorsMax")}</Hint>
      ) : (
        <Hint>{t("knobs.igSponsorsHint")}</Hint>
      )}
    </div>
  );
}

// ─── Instagram comments toggle ───────────────────────────────

export function InstagramCommentsKnob({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null | undefined;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("compose");
  return (
    <label className="flex items-center gap-2 text-xs">
      {/* Instagram's default is on, so an unset knob shows checked. */}
      <input
        type="checkbox"
        checked={value !== false}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="font-medium uppercase text-muted-foreground">{t("knobs.igComments")}</span>
    </label>
  );
}

// ─── Instagram location tag ──────────────────────────────────

export function InstagramLocationKnob({
  value,
  onChange,
  disabled,
}: {
  value: string | number | null | undefined;
  onChange: (next: string | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("compose");
  const text = value == null ? "" : String(value);
  const invalid = text !== "" && !/^\d+$/.test(text.trim());
  return (
    <div className="space-y-1">
      <Label>{t("knobs.igLocation")}</Label>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        disabled={disabled}
        placeholder="105890561436614"
        onChange={(e) => onChange(e.target.value.trim() || null)}
        className="block w-full rounded border px-3 py-2 text-sm font-mono"
      />
      <Hint tone={invalid ? "warn" : "muted"}>{invalid ? t("knobs.igLocationInvalid") : t("knobs.igLocationHint")}</Hint>
    </div>
  );
}

// ─── TikTok Commercial Music Library ─────────────────────────

export interface TikTokMusicKnobValue {
  musicSoundId: string;
  musicSoundVolume?: number;
  musicSoundStart?: number;
  musicSoundEnd?: number;
  title?: string | null;
  artist?: string | null;
}

const toMs = (raw: string): number | undefined => {
  if (raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) : undefined;
};
const toSec = (ms: number | undefined) => (ms === undefined ? "" : String(ms / 1000));

export function TikTokMusicKnob({
  brandId,
  value,
  onChange,
  originalVolume,
  onOriginalVolumeChange,
  isVideo,
  disabled,
}: {
  brandId: string;
  value: TikTokMusicKnobValue | null | undefined;
  onChange: (next: TikTokMusicKnobValue | null) => void;
  originalVolume: number | undefined;
  onOriginalVolumeChange: (v: number | undefined) => void;
  isVideo: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation("compose");
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState("");
  const list = useTikTokCommercialMusic(brandId, /^[A-Za-z]{2}$/.test(country) ? country.toUpperCase() : "", open && !disabled);
  const selectedId = value?.musicSoundId ?? null;

  const pick = (track: TikTokCommercialTrack) => {
    onChange({
      musicSoundId: track.id,
      title: track.name,
      artist: track.artist,
      ...(value?.musicSoundVolume !== undefined ? { musicSoundVolume: value.musicSoundVolume } : {}),
    });
    setOpen(false);
  };

  return (
    <div className="space-y-1">
      <Label>{t("knobs.ttMusic")}</Label>
      {selectedId ? (
        <div className="rounded border p-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{value?.title ?? selectedId}</div>
              {value?.artist && <div className="text-[11px] text-muted-foreground truncate">{value.artist}</div>}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setOpen(!open)}>
                {t("knobs.igAudioChange")}
              </Button>
              <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}>
                {t("knobs.remove")}
              </Button>
            </div>
          </div>
          {isVideo && (
            <>
              <div className="flex flex-wrap gap-3">
                <PercentInput
                  label={t("knobs.ttMusicVolume")}
                  value={value?.musicSoundVolume}
                  placeholder="50"
                  disabled={disabled}
                  onChange={(v) => onChange({ ...value!, musicSoundVolume: v })}
                />
                <PercentInput
                  label={t("knobs.ttOriginalVolume")}
                  value={originalVolume}
                  placeholder="50"
                  disabled={disabled}
                  onChange={onOriginalVolumeChange}
                />
              </div>
              <div className="flex flex-wrap gap-3">
                {(["musicSoundStart", "musicSoundEnd"] as const).map((key) => (
                  <label key={key} className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    {key === "musicSoundStart" ? t("knobs.ttTrimStart") : t("knobs.ttTrimEnd")}
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={toSec(value?.[key])}
                      disabled={disabled}
                      onChange={(e) => onChange({ ...value!, [key]: toMs(e.target.value) })}
                      className="w-16 rounded border px-1 py-0.5 text-xs"
                    />
                  </label>
                ))}
              </div>
              <Hint>{t("knobs.ttMusicHint")}</Hint>
            </>
          )}
        </div>
      ) : (
        !open && (
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setOpen(true)}>
            {t("knobs.ttMusicPick")}
          </Button>
        )
      )}

      {open && (
        <div className="rounded border p-2 space-y-2">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
              {t("knobs.ttCountry")}
              <input
                type="text"
                maxLength={2}
                value={country}
                placeholder="US"
                onChange={(e) => setCountry(e.target.value.toUpperCase())}
                className="w-12 rounded border px-1 py-0.5 text-xs uppercase"
              />
            </label>
            <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={() => setOpen(false)}>
              {t("knobs.close")}
            </Button>
          </div>
          <Hint>{t("knobs.trending")}</Hint>
          {list.isLoading && <Hint>{t("knobs.loading")}</Hint>}
          {list.isError && <Hint tone="warn">{t("knobs.loadFailed")}</Hint>}
          {list.data && !list.data.connected && <Hint tone="warn">{t("knobs.ttNotConnected")}</Hint>}
          {list.data?.unavailableReason && (
            <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-[11px] text-amber-800 dark:text-amber-300">
              {t("knobs.ttMusicUnavailable")} ({list.data.unavailableReason})
            </div>
          )}
          <div className="max-h-64 overflow-y-auto divide-y">
            {(list.data?.tracks ?? []).map((track) => (
              <div key={track.id} className="flex items-center gap-2 py-1.5">
                {track.thumbnailUrl ? (
                  <img src={track.thumbnailUrl} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-muted shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">
                    {track.rank ? `#${track.rank} ` : ""}
                    {track.name ?? track.id}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {[track.artist, track.genres.join(", ")].filter(Boolean).join(" · ")}
                  </div>
                  {track.previewUrl && (
                    <audio controls preload="none" src={track.previewUrl} className="w-full h-7 mt-1" />
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={track.id === selectedId ? "secondary" : "outline"}
                  onClick={() => pick(track)}
                >
                  {track.id === selectedId ? t("knobs.selected") : t("knobs.use")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
